import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getUsers, impersonate } from "../requests/impersonationRequests";
import {
  IImpersonatableUser,
  IImpersonationState,
} from "../interfaces/impersonation";
import { ROWS_PER_PAGE, TOKEN } from "../utils/constants";
import { IPaginationParams } from "../interfaces/table";
const ORIGINAL_TOKEN_KEY = "original_auth_token";
const IMPERSONATION_STATE_KEY = "impersonation_state";

export const useImpersonation = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState<IImpersonatableUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    count: 0,
    page: 0,
  } as IPaginationParams);
  const fetchUsers = useCallback(async (page = 0 as number, search = undefined as string | undefined) => {
    const skip = page * ROWS_PER_PAGE;
    setLoading(true);
    setError(null);
    try {
      const { users: fetchedUsers, total } = await getUsers(skip, ROWS_PER_PAGE, search);
      setUsers(fetchedUsers);
      setPagination({ count: total, page });
    } catch (err) {
      setError("Error al cargar la lista de usuarios");
    } finally {
      setLoading(false);
    }
  }, []);

  const startImpersonation = useCallback(
    async (user: IImpersonatableUser) => {
      setImpersonating(true);
      setError(null);
      try {
        const currentToken = localStorage.getItem(TOKEN);
        if (currentToken)
          localStorage.setItem(ORIGINAL_TOKEN_KEY, currentToken);

        const response = await impersonate(user.id);

        const impersonationState: IImpersonationState = {
          isImpersonating: true,
          impersonatedUser: user,
        };
        localStorage.setItem(
          IMPERSONATION_STATE_KEY,
          JSON.stringify(impersonationState),
        );

        localStorage.setItem(TOKEN, response.accessToken);

        window.location.href = "/";
      } catch (err) {
        setError("Error al impersonar usuario");
        console.error("Error impersonating user:", err);
      } finally {
        setImpersonating(false);
      }
    },
    [],
  );

  const stopImpersonation = useCallback(() => {
    const originalToken = localStorage.getItem(ORIGINAL_TOKEN_KEY);

    if (originalToken) {
      localStorage.setItem(TOKEN, originalToken);
      localStorage.removeItem(ORIGINAL_TOKEN_KEY);
    }

    localStorage.removeItem(IMPERSONATION_STATE_KEY);

    navigate("/impersonar-usuario");
    window.location.reload();
  }, [navigate]);

  const getImpersonationState = useCallback((): IImpersonationState => {
    const stored = localStorage.getItem(IMPERSONATION_STATE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return { isImpersonating: false, impersonatedUser: null };
      }
    }
    return { isImpersonating: false, impersonatedUser: null };
  }, []);

  const isImpersonating = useCallback((): boolean => {
    return getImpersonationState().isImpersonating;
  }, [getImpersonationState]);

  return {
    users,
    loading,
    impersonating,
    error,
    fetchUsers,
    startImpersonation,
    stopImpersonation,
    getImpersonationState,
    isImpersonating,
    pagination,
    setPagination,
  };
};
